import {
  PaidDataInput,
  StripeBillingInterval,
  StripePriceFragmentFragment,
} from '@/__generated__/graphql';
import {
  convertCentsToDollars,
  convertDollarsToCents,
} from '@/utils/convert-cents-to-dollars.ts';

// Computes total subscription price for custom streams.
// When customTargetsData is present, multiplies per-step price by the number of steps.
export const getSubscriptionTotalPrice = (
  subscription: { price: number; customTargets?: number | null },
  customTargetsData?: { stepSize?: number } | null,
): number => {
  if (
    customTargetsData &&
    subscription.customTargets &&
    customTargetsData.stepSize
  ) {
    const steps = subscription.customTargets / customTargetsData.stepSize;
    return Math.round(subscription.price * steps);
  }
  return subscription.price;
};

export const getMonthlyPrice = (
  paidData: StripePriceFragmentFragment[] | null | undefined,
): number | undefined => {
  if (!paidData?.length) return undefined;
  const monthly = paidData.find(
    (p) => p.interval === StripeBillingInterval.Month && p.intervalCount === 1,
  );
  return monthly?.amount ?? paidData[0].amount;
};

export const getPriceByInterval = (
  paidData: StripePriceFragmentFragment[] | null | undefined,
  interval: StripeBillingInterval,
  intervalCount: number,
): number | undefined => {
  return paidData?.find(
    (p) => p.interval === interval && p.intervalCount === intervalCount,
  )?.amount;
};

export const getYearlyPrice = (
  paidData: StripePriceFragmentFragment[] | null | undefined,
): number | undefined => {
  return (
    getPriceByInterval(paidData, StripeBillingInterval.Year, 1) ??
    getPriceByInterval(paidData, StripeBillingInterval.Month, 12)
  );
};

export const isYearlyInterval = (
  interval: StripeBillingInterval | string,
  intervalCount: number,
): boolean =>
  interval === StripeBillingInterval.Year ||
  (interval === StripeBillingInterval.Month && intervalCount === 12);

// Format without count for single periods: "month", "3 months", "year"
export const formatIntervalShort = (
  interval: StripeBillingInterval | string,
  intervalCount: number,
): string => {
  if (isYearlyInterval(interval, intervalCount)) return 'year';
  const label = interval.toLowerCase();
  if (intervalCount === 1) return label;
  return `${intervalCount} ${label}s`;
};

// Format with count for all periods: "1 month", "3 months", "1 year"
export const formatIntervalFull = (
  interval: StripeBillingInterval | string,
  intervalCount: number,
): string => {
  if (isYearlyInterval(interval, intervalCount)) return '1 year';
  const label = interval.toLowerCase();
  if (intervalCount === 1) return `1 ${label}`;
  return `${intervalCount} ${label}s`;
};

export type PaidDataFormValues = {
  monthlyPrice: number;
  threeMonthPrice?: number | null;
  sixMonthPrice?: number | null;
  yearlyPrice?: number | null;
};

export const buildPaidDataInput = (
  values: PaidDataFormValues,
): PaidDataInput => {
  const monthlyCents = convertDollarsToCents(values.monthlyPrice);

  return {
    prices: {
      monthlyPrice: monthlyCents,
      quarterlyPrice: values.threeMonthPrice
        ? convertDollarsToCents(values.threeMonthPrice)
        : monthlyCents * 3,
      semiAnnualPrice: values.sixMonthPrice
        ? convertDollarsToCents(values.sixMonthPrice)
        : monthlyCents * 6,
      annualPrice: values.yearlyPrice
        ? convertDollarsToCents(values.yearlyPrice)
        : monthlyCents * 12,
    },
  };
};

export const paidDataToFormValues = (
  paidData: StripePriceFragmentFragment[] | null | undefined,
): PaidDataFormValues | undefined => {
  if (!paidData?.length) return undefined;
  const monthly = getMonthlyPrice(paidData);
  const threeMonth = getPriceByInterval(
    paidData,
    StripeBillingInterval.Month,
    3,
  );
  const sixMonth = getPriceByInterval(paidData, StripeBillingInterval.Month, 6);
  const yearly = getYearlyPrice(paidData);
  return {
    monthlyPrice: monthly != null ? convertCentsToDollars(monthly) : 0,
    threeMonthPrice:
      threeMonth != null ? convertCentsToDollars(threeMonth) : undefined,
    sixMonthPrice:
      sixMonth != null ? convertCentsToDollars(sixMonth) : undefined,
    yearlyPrice: yearly != null ? convertCentsToDollars(yearly) : undefined,
  };
};
